/**
 * Sub-collection buttons on collection pages (markup: snippets/collection-subnav.liquid).
 *
 * - Clicking a button loads that sub-collection's products into this page without a page load;
 *   the address becomes the sub-collection's link, so refresh, sharing and Back keep it.
 * - Clicking the selected button again, or "Reset", brings back the whole collection.
 * - Sorting, filters and page links keep working inside the sub-collection, because the theme
 *   (assets/filter.js) always loads them from the current address.
 */
(() => {
	if (window.collectionSubnavReady) return;
	window.collectionSubnavReady = true;

	let requestId = 0;
	let shownPath = window.location.pathname;

	const wrapper = () => document.querySelector('.has-product-filters');

	const pathOf = (href) => {
		try {
			return decodeURIComponent(new URL(href, window.location.origin).pathname).toLowerCase().replace(/\/$/, '');
		} catch (error) {
			return '';
		}
	};

	/* ---------------------------------------------------------------- Button state */

	// Mark the button for the current address. The theme's own filtering can change the address
	// (e.g. "Clear" goes back to the whole collection) without re-drawing the buttons on phones.
	const syncButtons = () => {
		const current = pathOf(window.location.href);

		document.querySelectorAll('[data-collection-subnav]').forEach((nav) => {
			let active = null;

			nav.querySelectorAll('[data-subnav-link]:not([data-subnav-reset])').forEach((link) => {
				const isActive = pathOf(link.href) === current;
				link.classList.toggle('is-active', isActive);
				link.classList.remove('is-loading');
				if (isActive) {
					link.setAttribute('aria-current', 'page');
					active = link;
				} else {
					link.removeAttribute('aria-current');
				}
			});

			const reset = nav.querySelector('[data-subnav-reset]');
			if (reset) reset.closest('li').hidden = pathOf(reset.href) === current;

			// Phones: the buttons scroll sideways, so bring the selected one into view
			// (next to "Reset", which stays fixed at the start of the row)
			const list = nav.querySelector('.collection-subnav__list');
			if (active && list && list.scrollWidth > list.clientWidth) {
				const item = active.closest('li');
				const resetItem = reset && reset.closest('li');
				const covered = resetItem && !resetItem.hidden && getComputedStyle(resetItem).position === 'sticky' ? resetItem.offsetWidth : 0;
				if (item.offsetLeft + item.offsetWidth > list.scrollLeft + list.clientWidth || item.offsetLeft < list.scrollLeft + covered) {
					list.scrollLeft = Math.max(0, item.offsetLeft - (covered || 16));
				}
			}
		});
	};

	/* ---------------------------------------------------------------- Loading products */

	const setLoading = (loading) => {
		if (loading) document.documentElement.setAttribute('data-filtering', '');
		else document.documentElement.removeAttribute('data-filtering');
	};

	// Same set-up the theme runs after it re-draws the products and filters
	const setUp = (wrap) => {
		const run = (name, target) => {
			if (typeof window[name] !== 'function') return;
			try {
				window[name](target);
			} catch (error) {
				console.error(error);
			}
		};

		run('btyDesktopFilters', document);
		run('btyMobileFilters', document);
		['btyRangeSlider', 'btyToggleDetails', 'btyAnimationImageLoad', 'btyScrollAnimationTrigger', 'btyHoverMediaVideo', 'btyQuickView', 'btyQuickAdd', 'btySwatch', 'btyAccordionHandle', 'btyAddToCart'].forEach((name) => run(name, wrap));

		document.dispatchEvent(new CustomEvent('product-card-updated'));
	};

	const load = async (href, addToHistory) => {
		const wrap = wrapper();
		if (!wrap) {
			window.location.href = href;
			return;
		}

		const id = ++requestId;
		const url = new URL(href, window.location.origin);
		url.searchParams.set('section_id', wrap.dataset.id);
		setLoading(true);

		try {
			const response = await fetch(url.toString());
			if (!response.ok) throw new Error('Sub-collection: ' + response.status);

			const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
			const fresh = doc.querySelector('.has-product-filters');
			if (!fresh) throw new Error('Sub-collection: no products in the response');
			if (id !== requestId) return; // a newer choice replaced this one

			const hadFocus = Boolean(document.activeElement && document.activeElement.closest('[data-collection-subnav]'));

			wrap.innerHTML = fresh.innerHTML;
			if (addToHistory) window.history.pushState({ path: href }, '', href);
			shownPath = window.location.pathname;

			setUp(wrap);
			syncButtons();

			if (hadFocus) {
				const button = wrap.querySelector('[data-collection-subnav] .is-active') || wrap.querySelector('[data-collection-subnav] [data-subnav-link]');
				if (button) button.focus({ preventScroll: true });
			}
		} catch (error) {
			console.error(error);
			// Could not load it here: open the sub-collection as a normal page instead
			if (id === requestId && addToHistory) window.location.href = href;
		} finally {
			if (id === requestId) setLoading(false);
		}
	};

	/* ---------------------------------------------------------------- Events */

	document.addEventListener('click', (event) => {
		const link = event.target.closest('[data-collection-subnav] [data-subnav-link]');
		if (!link) return;

		// Ctrl/Cmd/middle click still open a new tab
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;

		event.preventDefault();

		const nav = link.closest('[data-collection-subnav]');
		const reset = nav.querySelector('[data-subnav-reset]');

		// The selected button works as a toggle: clicking it again shows the whole collection
		const target = new URL(link.classList.contains('is-active') && reset ? reset.href : link.href, window.location.origin);

		// Keep the chosen sort order; filters and page number start fresh
		const current = new URL(window.location.href);
		const sort = current.searchParams.get('sort_by');
		if (sort) target.searchParams.set('sort_by', sort);

		if (target.pathname + target.search === current.pathname + current.search) return;

		link.classList.add('is-loading');
		load(target.pathname + target.search, true);
	});

	// Back / Forward between sub-collections
	window.addEventListener('popstate', () => {
		if (!document.querySelector('[data-collection-subnav]')) return;
		if (window.location.pathname === shownPath) return; // the theme's own filter history: unchanged

		load(window.location.pathname + window.location.search, false);
	});

	// The theme re-draws products after its own filtering/sorting
	document.addEventListener('product-card-updated', () => {
		shownPath = window.location.pathname;
		syncButtons();
	});

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncButtons);
	else syncButtons();
})();
